import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import ProfileButton from './components/ProfileButton';
import PendingInvitationsBanner from './components/PendingInvitationsBanner';
import DomainAutoJoinBanner from './components/DomainAutoJoinBanner';
import SupportChatWidget from './components/SupportChatWidget';
import { Toaster } from 'react-hot-toast';

function App() {
  const { currentUser } = useAuth();
  const location = useLocation();
  
  // Don't show the header on the landing page (root path)
  const isLandingPage = location.pathname === '/';
  // Show full navigation header for Terms, Privacy, and Support pages
  const isTermsPage = location.pathname === '/terms';
  const isPrivacyPage = location.pathname === '/privacy';
  const isSupportPage = location.pathname === '/support';
  const isBlogPage = location.pathname.startsWith('/blog');
  // Show Dashboard link for authenticated pages (Dashboard, Interviewer, Settings, Membership, Terms, Privacy, Support, Blog)
  const isDashboardPage = location.pathname === '/dashboard';
  const isInterviewerPage = location.pathname.startsWith('/interviewer/');
  const isSettingsPage = location.pathname.startsWith('/settings');
  const isMembershipPage = location.pathname === '/membership';
  const isMeetPage = location.pathname.startsWith('/meet/');
  const showDashboardLink = isDashboardPage || isInterviewerPage || isSettingsPage || isMembershipPage || isTermsPage || isPrivacyPage || isSupportPage || isBlogPage;
  const showFullNavigation = isTermsPage || isPrivacyPage || isSupportPage || isBlogPage;

  return (
    <div className={`min-h-screen bg-gray-50`}>
      {/* Notification banners for logged-in users */}
      {currentUser && !location.pathname.startsWith('/team/invite') && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <PendingInvitationsBanner userEmail={currentUser.email} />
          <DomainAutoJoinBanner />
        </div>
      )}

      {!isLandingPage && !isMeetPage && (
        <header className="bg-white shadow-sm border-b border-gray-200">
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-6">
                <Link to="/" className="flex items-center space-x-2 text-xl font-bold text-gray-900 hover:text-blue-600 transition-colors">
                  <img src="/AppIcon.ico" alt="Interview Proctor" className="h-6 w-6" />
                  <span>Interview Proctor</span>
                </Link>
                {showDashboardLink && (
                  <Link
                    to="/dashboard"
                    className="text-lg font-semibold text-gray-800 hover:text-blue-600 transition-colors"
                  >
                    Dashboard
                  </Link>
                )}
              </div>
              <div className="flex items-center space-x-4">
                {showFullNavigation && (
                  <>
                    <a href="/#how-it-works" className="text-gray-700 hover:text-blue-600 transition-colors">
                      How It Works
                    </a>
                    <a href="/#pricing" className="text-gray-700 hover:text-blue-600 transition-colors">
                      Pricing
                    </a>
                    <a href="/#download" className="text-gray-700 hover:text-blue-600 transition-colors">
                      Download
                    </a>
                  </>
                )}
                {currentUser ? (
                  <ProfileButton />
                ) : (
                  <Link
                    to="/login"
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          </nav>
        </header>
      )}
      <main>
        <Outlet />
      </main>

      {/* Support Chat Widget - Only for signed-in users */}
      {currentUser && <SupportChatWidget />}

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
            minWidth: '390px', // 30% increase from default ~300px
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
}

export default App;
